import { useCallback, useEffect, useState } from 'react'
import req from '@sportninja/common/api/request'

/**
 * KPI payload shape returned by `GET /scheduler/ai-suggest/kpis`.
 * Phase 1 returns zeros; Phase 4+ this payload populates as customers
 * use the feature.
 */
export interface AiSuggestKpiPayload {
  unchanged_acceptance_rate: number
  acceptance_rate: number
  rejection_rate: number
  median_time_to_decide_seconds: number | null
  seven_day_regret_rate: number
  by_policy_org_uid: Record<
    string,
    { count: number; unchanged_acceptance_rate: number }
  >
  by_inherited: {
    specialized: { count: number; unchanged_acceptance_rate: number }
    inherited: { count: number; unchanged_acceptance_rate: number }
  }
  total_suggestions: number
  total_responded: number
  window_days: number
}

interface UseAiSuggestKpisResult {
  kpis: AiSuggestKpiPayload | null
  isLoading: boolean
  error: Error | null
  refresh: () => Promise<void>
}

/**
 * Hook that fetches AI Suggest KPI metrics on mount + on demand.
 * Used by the KPI dashboard tile (Phase 4) — see AiSuggestKpiCard.
 */
export const useAiSuggestKpis = (): UseAiSuggestKpisResult => {
  'use memo'

  const [kpis, setKpis] = useState<AiSuggestKpiPayload | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  const refresh = useCallback(async (): Promise<void> => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await req('/scheduler/ai-suggest/kpis')
      setKpis(response?.data ?? null)
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err
          : new Error('Failed to fetch AI Suggest KPI metrics.')
      )
      setKpis(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh().catch(() => {
      // surfaced via `error` state
    })
  }, [refresh])

  return { kpis, isLoading, error, refresh }
}
